import { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GridView as GridViewIcon,
  Undo as UndoIcon,
  ArrowDownward,
  ArrowUpward,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ActionButton from "./ActionButton";
import FilterwiseButton from "./FilterwiseButton";
import SearchBar from "./SearchBar";
import AddButton from "./AddButton";
import Pagination from "./Pagination";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import CheckIcon from "@mui/icons-material/Check";

const colors = {
  grey: { 50: "#f9fafb" },
  primary: { main: "#3b82f6", dark: "#1d4ed8" },
};

const DraggableTableRow = ({ row, children, id }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled: false // Allow dragging
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 999 : 1,
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      sx={{
        cursor: 'move', // Add cursor style
        '&:hover': {
          backgroundColor: '#f5f5f5',
        }
      }}
    >
      {children}
    </TableRow>
  );
};

const DataTableMUI = ({
  title,
  columns,
  data,
  searchable = true,
  pagination = true,
  itemsPerPage = 10,
  setItemsPerPage,
  currentPage = 1,
  totalItems = 0,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onUndo,
  onAdd,
  onAddClick,
  statusOptions,
  statusFilter = "all",
  onStatusFilterChange,
  sortKey,
  sortOrder,
  onSort,
  searchTerm,
  setSearchTerm,
  onSearch,
  draggable = false,
  extraData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);

  // Column visibility state
  const allColumnKeys = [
    ...columns.map((col) => col.accessor ?? col.header),
    ...(onView || onEdit || onDelete || onUndo ? ["Action"] : []),
  ];
  const [visibleCols, setVisibleCols] = useState(allColumnKeys);

  const handleOpenColMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseColMenu = () => setAnchorEl(null);
  const handleToggleCol = (accessor) =>
    setVisibleCols((prev) =>
      prev.includes(accessor)
        ? prev.filter((col) => col !== accessor)
        : [...prev, accessor]
    );

  const tableColumns = columns.filter((col) =>
    visibleCols.includes(col.accessor ?? col.header)
  );
  const showActionCol =
    visibleCols.includes("Action") && (onView || onEdit || onDelete || onUndo);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const TableContent = () => (
    <TableBody>
      {data.length === 0 ? (
        <TableRow>
          <TableCell
            colSpan={tableColumns.length + (showActionCol ? 1 : 0)}
            align="center"
            className="poppins-font" // Add this class
          >
            No data found.
          </TableCell>
        </TableRow>
      ) : draggable ? (
        <SortableContext
          items={data.map((item) => item._id)}
          strategy={verticalListSortingStrategy}
        >
          {data.map((row, idx) => (
            <DraggableTableRow key={row._id} id={row._id} row={row}>
              {tableColumns.map((col, cidx) => (
                <TableCell
                  key={col.accessor || cidx}
                  align="center"
                  className="poppins-font tableContent" // Add this class
                  sx={{ border: "1.5px solid #b0b6c3" }}
                >
                  {col.render
                    ? col.render(
                        row[col.accessor],
                        row,
                        idx,
                        currentPage,
                        itemsPerPage,
                        undefined,
                        extraData // Pass extraData as last argument
                      )
                    : row[col.accessor]}
                </TableCell>
              ))}
              {showActionCol && (
                <TableCell
                  align="center"
                  className="poppins-font" // Add this class
                  sx={{ border: "1.5px solid #b0b6c3" }}
                >
                  <Stack 
                    direction="row" 
                    justifyContent="center" 
                    spacing={1}
                    onClick={(e) => e.stopPropagation()} // Stop event propagation
                  >
                    {onView && (
                      <ActionButton
                        color1={theme.palette.info.main}
                        color2={theme.palette.info.dark}
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(row);
                        }}
                        title="View"
                      >
                        <VisibilityIcon fontSize="small" />
                      </ActionButton>
                    )}
                    {onEdit && row.status !== "deleted" && (
                      <ActionButton
                        color1={theme.palette.success.main}
                        color2={theme.palette.success.dark}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(row);
                        }}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </ActionButton>
                    )}
                    {extraData && extraData.onApproveClick && row.status === "pending" && (
                      <ActionButton
                        color1={theme.palette.primary.main}
                        color2={theme.palette.primary.dark}
                        onClick={(e) => {
                          e.stopPropagation();
                          extraData.onApproveClick(row);
                        }}
                        title="Approve"
                      >
                        <CheckIcon fontSize="small" />
                      </ActionButton>
                    )}
                    {onDelete && row.status !== "deleted" && (
                      <ActionButton
                        color1={theme.palette.error.main}
                        color2={theme.palette.error.dark}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row);
                        }}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </ActionButton>
                    )}
                    {onUndo && row.status === "deleted" && (
                      <ActionButton
                        color1={theme.palette.warning.main}
                        color2={theme.palette.warning.dark}
                        onClick={(e) => {
                          e.stopPropagation();
                          onUndo(row);
                        }}
                        title="Undo"
                      >
                        <UndoIcon fontSize="small" />
                      </ActionButton>
                    )}
                  </Stack>
                </TableCell>
              )}
            </DraggableTableRow>
          ))}
        </SortableContext>
      ) : (
        data.map((row, idx) => (
          <TableRow
            key={row.id || idx}
            className="poppins-font"
            sx={{
              background: idx % 2 === 0 ? colors.grey[50] : "#fff",
              border: "1.5px solid #b0b6c3",
            }}
          >
            {tableColumns.map((col, cidx) => (
              <TableCell
                key={col.accessor || cidx}
                align="center"
                className="poppins-font"
                sx={{ border: "1.5px solid #b0b6c3" }}
              >
                {col.render
                  ? col.render(
                      row[col.accessor],
                      row,
                      idx,
                      currentPage,
                      itemsPerPage
                    )
                  : row[col.accessor]}
              </TableCell>
            ))}
            {showActionCol && (
              <TableCell
                align="center"
                className="poppins-font"
                sx={{ border: "1.5px solid #b0b6c3" }}
              >
                <Stack 
                  direction="row" 
                  justifyContent="center" 
                  spacing={1}
                  onClick={(e) => e.stopPropagation()} // Stop event propagation
                >
                  {onView && (
                    <ActionButton
                      color1={theme.palette.info.main}
                      color2={theme.palette.info.dark}
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(row);
                      }}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </ActionButton>
                  )}
                  {onEdit && row.status !== "deleted" && (
                    <ActionButton
                      color1={theme.palette.success.main}
                      color2={theme.palette.success.dark}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(row);
                      }}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </ActionButton>
                  )}
                  {extraData && extraData.onApproveClick && row.status === "pending" && (
                    <ActionButton
                      color1={theme.palette.primary.main}
                      color2={theme.palette.primary.dark}
                      onClick={(e) => {
                        e.stopPropagation();
                        extraData.onApproveClick(row);
                      }}
                      title="Approve"
                    >
                      <CheckIcon fontSize="small" />
                    </ActionButton>
                  )}
                  {onDelete && row.status !== "deleted" && (
                    <ActionButton
                      color1={theme.palette.error.main}
                      color2={theme.palette.error.dark}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(row);
                      }}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </ActionButton>
                  )}
                  {onUndo && row.status === "deleted" && (
                    <ActionButton
                      color1={theme.palette.warning.main}
                      color2={theme.palette.warning.dark}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUndo(row);
                      }}
                      title="Undo"
                    >
                      <UndoIcon fontSize="small" />
                    </ActionButton>
                  )}
                </Stack>
              </TableCell>
            )}
          </TableRow>
        ))
      )}
    </TableBody>
  );

  return (
    <Paper className="data-table" sx={{ p: 2, m: 2, borderRadius: 5 }}>
      <Box sx={{ mb: 2 }}>
        <Typography 
          className="poppins-font" 
          variant="h6" 
          fontWeight={700}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          gap: 1,
          pb: 1,
          mb: 2,
        }}
      >
        <FilterwiseButton
          statusFilter={statusFilter}
          statusOptions={statusOptions}
          onStatusFilterChange={onStatusFilterChange}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          flexDirection: "row",
          width: "100%",
        }}
      >
        {searchable && (
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSearch={onSearch}
          />
        )}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          {onAdd && <AddButton onAddClick={onAddClick} isMobile={isMobile} />}
          <IconButton
            onClick={handleOpenColMenu}
            sx={{
              background: "#f3f4f6",
              color: theme.palette.primary.main,
              borderRadius: "50%",
              "&:hover": {
                background: theme.palette.primary.main,
                color: "#fff",
              },
              fontSize: { xs: 18, sm: 22 },
            }}
            title="Show/Hide Columns"
          >
            <GridViewIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseColMenu}
          >
            {columns.map((col) => (
              <MenuItem
                key={col.accessor ?? col.header}
                onClick={() => handleToggleCol(col.accessor ?? col.header)}
                className="poppins-font" // Add this class
              >
                <Checkbox
                  checked={visibleCols.includes(col.accessor ?? col.header)}
                  size="small"
                />
                <ListItemText 
                  primary={col.header}
                  className="poppins-font" // Add this class
                />
              </MenuItem>
            ))}
            {(onView || onEdit || onDelete || onUndo) && (
              <MenuItem key="Action" onClick={() => handleToggleCol("Action")}>
                <Checkbox checked={showActionCol} size="small" />
                <ListItemText primary="Action" />
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      <Box>
        <Table className="poppins-font">
          <TableHead className="poppins-font">
            <TableRow className="poppins-font">
              {tableColumns.map((col, idx) => (
                <TableCell
                  key={col.accessor || idx}
                  align="center"
                  className="poppins-font"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.80em", // smaller heading font
                    border: "1.5px solid #b0b6c3",
                    background: "#e3e8f7",
                    cursor: col.sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  onClick={() => onSort && onSort(col)}
                >
                  {col.header}
                  {col.sortable && (
                    <>
                      {sortKey === col.accessor ? (
                        sortOrder === "asc" ? (
                          <ArrowUpward
                            fontSize="small"
                            sx={{ ml: 0.5, verticalAlign: "middle" }}
                          />
                        ) : (
                          <ArrowDownward
                            fontSize="small"
                            sx={{ ml: 0.5, verticalAlign: "middle" }}
                          />
                        )
                      ) : (
                        <ArrowUpward
                          fontSize="small"
                          sx={{
                            ml: 0.5,
                            opacity: 0.2,
                            verticalAlign: "middle",
                          }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
              ))}
              {showActionCol && (
                <TableCell
                  className="poppins-font"
                  align="center"
                  sx={{
                    fontWeight: 700,
                    border: "1.5px solid #b0b6c3",
                    background: "#e3e8f7",
                  }}
                >
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableContent />
        </Table>
      </Box>

      {pagination && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={onPageChange}
          totalPages={totalPages}
          getPagination={() => {
            const pages = [];
            if (totalPages <= 5)
              for (let i = 1; i <= totalPages; i++) pages.push(i);
            else if (currentPage <= 3)
              pages.push(1, 2, 3, 4, "...", totalPages);
            else if (currentPage >= totalPages - 2)
              pages.push(
                1,
                "...",
                totalPages - 3,
                totalPages - 2,
                totalPages - 1,
                totalPages
              );
            else
              pages.push(
                1,
                "...",
                currentPage - 1,
                currentPage,
                currentPage + 1,
                "...",
                totalPages
              );
            return pages;
          }}
          itemsPerPageState={itemsPerPage}
          setItemsPerPageState={setItemsPerPage}
          setItemsPerPage={() => {}}
          sortedData={data}
          totalItems={totalItems}
        />
      )}
    </Paper>
  );
};

export default DataTableMUI;
